import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getToken } from '../hooks/useApi.js';
import { uiConfirm } from '../utils/dialogs.js';

/**
 * Order Files — import Shopify / Wix / Submariners exports and wholesale
 * purchase-order PDFs (THE PIG), check them, and turn them into DRAFT
 * fulfilment / sales orders. Replaces the stand-alone Order SKU Counter.
 * Files attached to orders@ emails land here automatically.
 */

// ── Small primitives (same look as Fulfilment.jsx) ────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: props.disabled ? 'not-allowed' : 'pointer', border: 'none', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1, whiteSpace: 'nowrap' };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' }, link: { background: 'none', color: 'var(--pld-green)', padding: 0, textDecoration: 'underline' } };
  return <button type="button" {...props} style={{ ...base, ...(v[variant] || v.primary), ...props.style }}>{children}</button>;
}
function Modal({ title, onClose, width = 560, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>&#215;</button>
        </div>
        <div style={{ padding: 22 }}>{children}</div>
      </div>
    </div>
  );
}
const inputStyle = { padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' };
function Input(props) { return <input {...props} style={{ ...inputStyle, ...props.style }} />; }
function Select({ children, ...props }) { return <select {...props} style={{ ...inputStyle, ...props.style }}>{children}</select>; }
function Field({ label, children }) { return <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 500 }}>{label}{children}</label>; }
const fmtDT = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const pill = (bg, fg) => ({ display: 'inline-block', padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: bg, color: fg, whiteSpace: 'nowrap' });
const FILE_STATUS = { review: ['To review', '#fef3c7', '#92400e'], completed: ['Done', '#dcfce7', '#166534'], discarded: ['Discarded', '#f3f4f6', '#6b7280'], failed: ["Couldn't read", '#fee2e2', '#991b1b'] };

// ── File list ─────────────────────────────────────────────────────────────
export function OrderFilesView({ openFileId, onOpened }) {
  const [filter, setFilter] = useState('review');
  const [files, setFiles] = useState(null);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(openFileId || null);

  useEffect(() => { if (openFileId) { setOpenId(openFileId); onOpened?.(); } }, [openFileId, onOpened]);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`/orders/imports${filter !== 'all' ? `?status=${filter}` : ''}`);
      setFiles(res.data ?? []); setErr('');
    } catch (e) { setErr(e.message); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  async function upload(e) {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (!list.length) return;
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      list.forEach(f => fd.append('files', f));
      const res = await fetch('/api/orders/imports', { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Upload failed (HTTP ${res.status})`);
      const results = body.data || [];
      if (results.length === 1 && results[0].status === 'review') setOpenId(results[0].id);
      else { setFilter('all'); await load(); }
    } catch (e2) { setErr(e2.message); } finally { setUploading(false); }
  }

  if (openId) return <ImportDetail fileId={openId} onBack={() => { setOpenId(null); load(); }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['review', 'To review'], ['completed', 'Done'], ['all', 'All']].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: filter === k ? 'var(--pld-green)' : 'var(--surface-2)', color: filter === k ? '#fff' : 'var(--text-muted)' }}>{l}</button>
          ))}
        </div>
        <label style={{ ...pill('var(--pld-green)', '#fff'), padding: '9px 18px', fontSize: 14, borderRadius: 'var(--radius)', cursor: uploading ? 'wait' : 'pointer' }}>
          {uploading ? 'Reading…' : '⬆ Import order files'}
          <input type="file" multiple accept=".csv,.xlsx,.pdf" onChange={upload} disabled={uploading} style={{ display: 'none' }} />
        </label>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px' }}>
        Shopify and Wix exports, Submariners spreadsheets and wholesale purchase-order PDFs. Files attached to emails at orders@ appear here when you check the inbox.
      </p>
      {err && <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p>}
      {!files ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : !files.length ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
          {filter === 'review' ? 'Nothing waiting to review.' : 'No order files yet.'}
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: 'var(--surface-2)' }}>
              {['Received', 'From', 'File', 'Type', 'Orders', 'Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {files.map(f => {
                const [label, bg, fg] = FILE_STATUS[f.status] || [f.status, '#eee', '#333'];
                return (
                  <tr key={f.id} onClick={() => setOpenId(f.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDT(f.created_at)} {f.source === 'email' ? '📧' : ''}</td>
                    <td style={{ padding: '10px 14px' }}>{(() => { const w = senderOf(f); return <>{w.main}{w.sub && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{w.sub}</div>}</>; })()}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{f.filename}{f.subject_line && <div style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)' }}>{f.subject_line}</div>}{f.error && <div style={{ fontWeight: 400, fontSize: 12, color: f.status === 'failed' ? '#991b1b' : 'var(--text-muted)' }}>{f.error}</div>}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{f.format_label || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>{f.order_count ? `${f.created_count}/${f.order_count} created` : '—'}</td>
                    <td style={{ padding: '10px 14px' }}><span style={pill(bg, fg)}>{label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Who a file came from: the email's sender, or who uploaded it. */
function senderOf(f) {
  if (f.source === 'email') {
    if (f.from_name && f.from_email && f.from_name !== f.from_email) return { main: f.from_name, sub: f.from_email };
    return { main: f.from_email || f.from_name || 'Unknown sender', sub: null };
  }
  return { main: f.uploaded_by_name ? `Uploaded by ${f.uploaded_by_name}` : 'Uploaded', sub: null };
}

// ── One file under review ─────────────────────────────────────────────────
function ImportDetail({ fileId, onBack }) {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [mapping, setMapping] = useState(null);   // { line, order }
  const [editing, setEditing] = useState(null);   // order
  const [pickingOutlet, setPickingOutlet] = useState(null); // order

  const load = useCallback(async () => {
    try { const res = await apiFetch(`/orders/imports/${fileId}`); setData(res.data); setErr(''); }
    catch (e) { setErr(e.message); }
  }, [fileId]);
  useEffect(() => { load(); apiFetch('/orders/imports/options/clients').then(r => setClients(r.data || [])).catch(() => {}); }, [load]);

  async function patchOrder(order, body) {
    try { await apiFetch(`/orders/imports/orders/${order.id}`, { method: 'PATCH', body }); await load(); }
    catch (e) { setErr(e.message); }
  }
  async function claimStock(order) {
    if (!(await uiConfirm(`Mark the products on ${order.external_ref} as ${order.client_name}'s stock?\n\nThey'll move from PLD stock to ${order.client_name} in Blake (Stock → item → Owned by), and can then go on their fulfilment orders.`))) return;
    try {
      const r = await apiFetch(`/orders/imports/orders/${order.id}/claim-stock`, { method: 'POST', body: {} });
      const d = r.data || {};
      setErr(d.left?.length ? `These belong to a different client and were left alone: ${d.left.join(', ')}` : '');
      await load();
    } catch (e) { setErr(e.message); }
  }
  async function setFileClient(clientId) {
    try { await apiFetch(`/orders/imports/${fileId}/client`, { method: 'PATCH', body: { client_id: clientId, overwrite: true } }); await load(); }
    catch (e) { setErr(e.message); }
  }
  async function createOrders() {
    setBusy(true); setErr(''); setResult(null);
    try { const res = await apiFetch(`/orders/imports/${fileId}/create`, { method: 'POST', body: {} }); setResult(res.data); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function discard() {
    if (!(await uiConfirm('Discard this file? Orders already created from it are kept.'))) return;
    try { await apiFetch(`/orders/imports/${fileId}/discard`, { method: 'POST', body: {} }); onBack(); }
    catch (e) { setErr(e.message); }
  }
  async function retry() {
    setErr('');
    try { const r = await apiFetch(`/orders/imports/${fileId}/retry`, { method: 'POST', body: {} }); setData(r.data); }
    catch (e) { setErr(e.message); }
  }
  async function downloadOriginal() {
    const res = await fetch(`/api/orders/imports/${fileId}/file`, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) { setErr('Could not download the file'); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a'); a.href = url; a.download = data.filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  if (!data) return <div><Btn variant="ghost" size="sm" onClick={onBack}>← Back</Btn>{err ? <p style={{ color: '#dc2626' }}>{err}</p> : <p style={{ color: 'var(--text-muted)' }}>Loading…</p>}</div>;

  const s = data.summary;
  const reviewing = data.status === 'review';
  const fileClientIds = [...new Set(data.orders.filter(o => o.kind === 'fulfilment' && o.status === 'pending').map(o => o.client_id || ''))];
  const fileClient = fileClientIds.length === 1 ? fileClientIds[0] : '';

  return (
    <div>
      {mapping && <MatchLineModal line={mapping.line} order={mapping.order} onClose={() => setMapping(null)} onDone={async () => { setMapping(null); await load(); }} />}
      {editing && <EditCustomerModal order={editing} onClose={() => setEditing(null)} onSave={async (customer) => { const o = editing; setEditing(null); await patchOrder(o, { customer }); }} />}
      {pickingOutlet && <OutletModal order={pickingOutlet} onClose={() => setPickingOutlet(null)} onPick={async (id) => { const o = pickingOutlet; setPickingOutlet(null); await patchOrder(o, { outlet_id: id }); }} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Btn variant="ghost" size="sm" onClick={onBack}>← Order files</Btn>
        <h2 style={{ margin: 0, fontSize: 17 }}>{data.filename}</h2>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{data.format_label} · {data.source === 'email' ? `from ${senderOf(data).main}${senderOf(data).sub ? ` <${senderOf(data).sub}>` : ''}` : senderOf(data).main} · {fmtDT(data.created_at)}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Btn variant="ghost" size="sm" onClick={downloadOriginal}>Original file</Btn>
          {data.status === 'failed' && <Btn size="sm" onClick={retry}>Try again</Btn>}
          {reviewing && <Btn variant="ghost" size="sm" onClick={discard} style={{ color: '#dc2626' }}>Discard</Btn>}
        </span>
      </div>

      {data.error && <p style={{ background: data.status === 'failed' ? '#fee2e2' : 'var(--surface-2)', color: data.status === 'failed' ? '#991b1b' : 'var(--text-muted)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13 }}>{data.error}</p>}
      {Array.isArray(data.warnings) && data.warnings.length > 0 && (
        <div style={{ background: '#fef3c7', color: '#92400e', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 12 }}>
          {data.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}
      {err && <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p>}
      {result && (
        <p style={{ background: result.failed ? '#fef3c7' : '#dcfce7', color: result.failed ? '#92400e' : '#166534', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13 }}>
          Created {result.created} draft order{result.created === 1 ? '' : 's'}{data.kind === 'sales' ? ' — see Sales Orders.' : ' — see the Fulfilment Orders tab.'}
          {result.failed ? ` ${result.failed} couldn't be created — see the red notes below.` : ''}
        </p>
      )}

      {data.orders.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ fontSize: 13 }}>
              <strong>{s.total}</strong> orders · <strong>{s.units}</strong> units · <span style={{ color: '#166534' }}>{s.ready} ready</span>
              {s.needs_attention > 0 && <> · <span style={{ color: '#b45309' }}>{s.needs_attention} need attention</span></>}
              {s.excluded > 0 && <> · {s.excluded} unticked</>}
              {s.created > 0 && <> · {s.created} created</>}
            </span>
            {reviewing && data.kind === 'fulfilment' && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginLeft: 'auto' }}>
                Client for this file
                <Select value={fileClient} onChange={e => setFileClient(e.target.value)} style={{ width: 220, padding: '6px 9px' }}>
                  <option value="">{fileClientIds.length > 1 ? '— several —' : '— choose —'}</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </span>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.orders.map(o => (
              <OrderCard key={o.id} o={o} reviewing={reviewing}
                onToggle={() => patchOrder(o, { include: !o.include })}
                onEdit={() => setEditing(o)}
                onMatch={(line) => setMapping({ line, order: o })}
                onPickOutlet={() => setPickingOutlet(o)}
                onClaim={() => claimStock(o)}
                onAllowDuplicate={async (on) => {
                  if (on && !(await uiConfirm(`${o.external_ref} is already in Blake as ${o.duplicate_of}. Send it out again as a second order?`, { okLabel: 'Send it again' }))) return;
                  patchOrder(o, { allow_duplicate: on });
                }} />
            ))}
          </div>

          {reviewing && (
            <div style={{ position: 'sticky', bottom: 0, background: 'var(--surface)', borderTop: '1px solid var(--border)', padding: '12px 0', marginTop: 12, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
              {s.needs_attention > 0 && <span style={{ fontSize: 13, color: '#b45309' }}>Orders needing attention are left here until they're fixed.</span>}
              <Btn onClick={createOrders} disabled={busy || s.ready === 0}>{busy ? 'Creating…' : `Create ${s.ready} draft order${s.ready === 1 ? '' : 's'}`}</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OrderCard({ o, reviewing, onToggle, onEdit, onMatch, onPickOutlet, onClaim, onAllowDuplicate }) {
  const c = o.customer || {};
  const created = o.status === 'created';
  const editable = reviewing && o.status === 'pending';
  const flags = o.flags || {};
  const border = created ? '#86efac' : o.status === 'pending' && o.include && !o.ready ? '#fcd34d' : 'var(--border)';
  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 'var(--radius-lg)', padding: '10px 14px', background: o.include || created ? 'var(--surface)' : 'var(--surface-2)', opacity: !o.include && !created ? 0.7 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        {editable && <input type="checkbox" checked={o.include} onChange={onToggle} style={{ marginTop: 3 }} title="Include this order" />}
        <div style={{ minWidth: 190, flex: '0 0 auto' }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{o.external_ref}</div>
          <div style={{ fontSize: 13 }}>{c.name}{c.company && c.company !== c.name ? ` · ${c.company}` : ''}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{[c.address1, c.city, c.postcode].filter(Boolean).join(', ')}</div>
          {editable && o.kind === 'fulfilment' && <Btn variant="link" size="sm" onClick={onEdit} style={{ fontSize: 12 }}>Edit address</Btn>}
        </div>
        <div style={{ flex: 1, minWidth: 260 }}>
          {o.lines.map(l => (
            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '2px 0', flexWrap: 'wrap' }}>
              <strong style={{ minWidth: 28 }}>{l.quantity}×</strong>
              <span style={{ color: 'var(--text-muted)' }}>{l.raw_name || l.raw_sku}{l.raw_sku && l.raw_name ? ` (${l.raw_sku})` : ''}{l.unit_text ? ` · ${l.unit_text}` : ''}{l.unit_price != null && o.kind === 'sales' && !l.price_from_list ? ` · £${Number(l.unit_price).toFixed(2)}` : ''}</span>
              <span>→</span>
              {l.stock_item_id
                ? <span><code style={{ fontSize: 12 }}>{l.item_sku}</code> {l.item_name}{editable && <Btn variant="link" size="sm" onClick={() => onMatch(l)} style={{ fontSize: 11, marginLeft: 6 }}>change</Btn>}</span>
                : editable
                  ? <Btn size="sm" onClick={() => onMatch(l)} style={{ background: '#f59e0b' }}>Match product…</Btn>
                  : <span style={{ color: '#b45309' }}>not matched</span>}
              {o.kind === 'sales' && l.price_from_list && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }} title={l.discount ? `List £${Number(l.list_price).toFixed(2)} less £${Number(l.discount).toFixed(2)} outlet discount` : 'From the price list'}>
                  · <strong style={{ color: 'var(--text)' }}>£{Number(l.unit_price).toFixed(2)}</strong>{l.discount ? ` (list £${Number(l.list_price).toFixed(2)} − £${Number(l.discount).toFixed(2)})` : ''}
                  {l.file_price != null && Math.abs(l.file_price - l.unit_price) > 0.005 && <span style={{ color: '#b45309' }}> · file says £{l.file_price.toFixed(2)}</span>}
                </span>
              )}
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'right', minWidth: 150 }}>
          {o.kind === 'fulfilment'
            ? <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{o.client_name || '—'}</div>
            : <div style={{ fontSize: 12 }}>{o.outlet_name ? <>{o.outlet_name} <code>{o.outlet_code}</code></> : <span style={{ color: '#b45309' }}>no outlet</span>}
                {editable && <div><Btn variant="link" size="sm" onClick={onPickOutlet} style={{ fontSize: 11 }}>{o.outlet_id ? 'change outlet' : 'choose outlet…'}</Btn></div>}</div>}
          {created && <span style={pill('#dcfce7', '#166534')}>✓ {o.fulfilment_order_ref || o.sales_order_ref}</span>}
          {o.status === 'skipped' && <span style={pill('#f3f4f6', '#6b7280')}>Skipped</span>}
          {(flags.cancelled || flags.refunded) && <div><span style={pill('#fee2e2', '#991b1b')}>{flags.cancelled ? 'Cancelled' : 'Refunded'} in source</span></div>}
          {flags.alreadyFulfilled && <div><span style={pill('#e0e7ff', '#3730a3')}>Already fulfilled in source</span></div>}
          {o.status === 'pending' && o.duplicate_of && flags.allow_duplicate && <div><span style={pill('#fef3c7', '#92400e')}>Sending again (also {o.duplicate_of})</span>{editable && <Btn variant="link" size="sm" onClick={() => onAllowDuplicate(false)} style={{ fontSize: 11 }}>undo</Btn>}</div>}
        </div>
      </div>
      {o.status === 'pending' && o.include && o.issues.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#b45309', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>⚠ {o.issues.join(' · ')}</span>
          {editable && o.client_name && o.issues.some(i => i.includes("client's stock")) && (
            <Btn size="sm" variant="ghost" onClick={onClaim}>Make these {o.client_name}'s stock</Btn>
          )}
          {editable && o.duplicate_of && !flags.allow_duplicate && (
            <Btn size="sm" variant="ghost" onClick={() => onAllowDuplicate(true)}>Send it again anyway</Btn>
          )}
        </div>
      )}
      {o.error_message && o.status === 'pending' && <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b' }}>Couldn't create: {o.error_message}</div>}
    </div>
  );
}

function MatchLineModal({ line, order, onClose, onDone }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      apiFetch(`/orders/imports/options/stock?q=${encodeURIComponent(q)}${order.client_id ? `&client_id=${order.client_id}` : ''}`)
        .then(r => setItems(r.data || [])).catch(e => setErr(e.message));
    }, 200);
    return () => clearTimeout(t);
  }, [q, order.client_id]);
  async function pick(item) {
    try {
      await apiFetch(`/orders/imports/lines/${line.id}/map`, { method: 'POST', body: { stock_item_id: item.id, remember } });
      onDone();
    } catch (e) { setErr(e.message); }
  }
  const label = line.raw_sku || line.raw_name;
  return (
    <Modal title="Match to a Blake product" onClose={onClose}>
      <p style={{ margin: '0 0 12px', fontSize: 13 }}>The file says <strong>{line.raw_name || '—'}</strong>{line.raw_sku ? <> with code <code>{line.raw_sku}</code></> : ''}.</p>
      <Input autoFocus placeholder="Search by name, Blake SKU or Brewman code…" value={q} onChange={e => setQ(e.target.value)} />
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, margin: '10px 0' }}>
        <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
        Remember: always match “{label}” to this product in future files
      </label>
      {err && <p style={{ color: '#dc2626', fontSize: 13 }}>{err}</p>}
      <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
        {items.map(it => (
          <div key={it.id} onClick={() => pick(it)} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 13 }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-2)')} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
            <div style={{ fontWeight: 600 }}>{it.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              <code>{it.sku}</code>{it.legacy_sku ? ` · ${it.legacy_sku}` : ''} · {it.client_name || 'PLD stock'}
              {order.kind === 'fulfilment' && order.client_id && it.owner_client_id !== order.client_id && <span style={{ color: '#b45309' }}> — not {order.client_name || "this client"}'s</span>}
            </div>
          </div>
        ))}
        {!items.length && <p style={{ padding: 12, margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>No products found.</p>}
      </div>
    </Modal>
  );
}

function EditCustomerModal({ order, onClose, onSave }) {
  const [c, setC] = useState({ ...order.customer });
  const set = (k) => (e) => setC(x => ({ ...x, [k]: e.target.value }));
  return (
    <Modal title={`Delivery details — ${order.external_ref}`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Name"><Input value={c.name || ''} onChange={set('name')} /></Field>
        <Field label="Company"><Input value={c.company || ''} onChange={set('company')} /></Field>
        <Field label="Address line 1"><Input value={c.address1 || ''} onChange={set('address1')} /></Field>
        <Field label="Address line 2"><Input value={c.address2 || ''} onChange={set('address2')} /></Field>
        <Field label="Town / city"><Input value={c.city || ''} onChange={set('city')} /></Field>
        <Field label="Postcode"><Input value={c.postcode || ''} onChange={set('postcode')} /></Field>
        <Field label="Email"><Input value={c.email || ''} onChange={set('email')} /></Field>
        <Field label="Phone"><Input value={c.phone || ''} onChange={set('phone')} /></Field>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={() => onSave(c)}>Save</Btn>
      </div>
    </Modal>
  );
}

function OutletModal({ order, onClose, onPick }) {
  const [q, setQ] = useState((order.customer?.name || '').replace(/^the\s+/i, '').replace(/-/g, ' ').trim());
  const [items, setItems] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => { apiFetch(`/orders/imports/options/outlets?q=${encodeURIComponent(q)}`).then(r => setItems(r.data || [])).catch(() => {}); }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Modal title={`Which outlet is “${order.customer?.name || order.external_ref}”?`} onClose={onClose}>
      <Input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search outlets by name or code…" />
      <div style={{ maxHeight: 340, overflow: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginTop: 10 }}>
        {items.map(o => (
          <div key={o.id} onClick={() => onPick(o.id)} style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 13 }}>
            <strong>{o.name}</strong> <code style={{ fontSize: 12 }}>{o.account_ref}</code> <span style={{ color: 'var(--text-muted)' }}>{o.postcode}</span>
          </div>
        ))}
        {!items.length && <p style={{ padding: 12, margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>No outlets found.</p>}
      </div>
    </Modal>
  );
}
